import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
  State,
  getAssetPath,
  Method
} from "@stencil/core";
import { Locale } from "../../../common/locale";
import {
  convertSuggestedDocumentsToSuggestItems,
  taskDocumentsToActionListItemModel
} from "./helpers";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { ActionListItemModel } from "@genexus/chameleon-controls-library/dist/types/components/action-list/types";
import { ComboBoxItemModel } from "@genexus/chameleon-controls-library/dist/types/components/combo-box/types";
import { ChComboBoxRenderCustomEvent } from "@genexus/chameleon-controls-library";

const CHECKED_VALUE = "true";
const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/list-box",
  "components/tabular-grid",
  "utils/form--full",
  "utils/layout",
  "utils/spacing",
  "chameleon/scrollbar"
];
const MODULE_ICON = getIconPath({
  category: "objects",
  name: "module"
});
const MORE_ICON = getIconPath({
  category: "gemini-tools",
  name: "show-more-horizontal",
  colorType: "primary"
});
const DELETE_ICON = getIconPath({
  category: "menus",
  name: "delete",
  colorType: "primary"
});

const NODENAME = "CH-CHECKBOX";
@Component({
  tag: "gx-ide-bpm-task-documents",
  styleUrl: "task-documents.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/bpm-task-documents"]
})
export class GxIdeBpmTaskDocuments {
  #_componentLocale: any;
  #renderedFirstTime = false;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/bpm-task-documents/shortcuts.json`
  );

  @Element() el: HTMLGxIdeBpmTaskDocumentsElement;

  #chShortcutsEl: HTMLChShortcutsElement;
  #deleteEnabledCheckboxEl: HTMLChCheckboxElement;
  #deleteRequiredCheckboxEl: HTMLChCheckboxElement;

  #newEnabledCheckboxEl: HTMLChCheckboxElement;
  #newRequiredCheckboxEl: HTMLChCheckboxElement;
  #readEnabledCheckboxEl: HTMLChCheckboxElement;
  #readRequiredCheckboxEl: HTMLChCheckboxElement;
  #updateEnabledCheckboxEl: HTMLChCheckboxElement;
  #updateRequiredCheckboxEl: HTMLChCheckboxElement;

  /**
   * Documents state for the action list
   */
  @State() documentsActionListState: ActionListItemModel[] = [];

  @State() documentName: string = null;

  /**
   * Document selected in the action list
   */
  @State() selectedDocument: TaskDocumentData = null;

  /**
   * Possible suggestions for the documents
   */
  @State() suggestedDocuments: ComboBoxItemModel[];

  /**
   * @property documents
   * @description The current value of the documents for the task
   */

  @Prop({ mutable: true }) documents: TaskDocumentData[];

  /**
   * @property cancelCallback
   * @description Callback invoked when the user cancels the application declaration (Cancel button)
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * @property confirmCallback
   * @description Callback invoked when the user confirms the application declaration (Ok button)
   */
  @Prop() readonly confirmCallback: ConfirmCallback;

  /**
   * @property selectDocumentCallback
   * @description Callback invoked when the user presses the '...' button to access the WorkflowDocuments selection dialog. Returns the name of the document to be inserted in the input or 'undefined' if it was canceled.
   */
  @Prop() readonly selectDocumentCallback: SelectDocumentCallback;

  /**
   * @property suggestDocumentsCallback
   * @description Callback that must be called to obtain options for the document input.
   */
  @Prop() readonly suggestDocumentsCallback: SuggestDocumentsCallback;

  /**
   * @description Gets fired when the component has rendered for the first time.
   */
  @Event() componentDidRenderFirstTime: EventEmitter<string>;

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    this.#refreshDocumentsActionListState();
  }

  componentDidRender() {
    if (!this.#renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this.#_componentLocale.componentName
      );
      this.#renderedFirstTime = true;
    }
    const shadowRoot = this.el.shadowRoot;
    const checkboxes = this.#getAllCheckboxes(
      shadowRoot as unknown as HTMLElement
    );
    if (checkboxes) {
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener(
          "input",
          this.#handleCheckboxChange.bind(this)
        );
      });
    }
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #getAllCheckboxes(node: HTMLElement): HTMLChCheckboxElement[] {
    const checkboxes: HTMLChCheckboxElement[] = [];

    node.childNodes.forEach(child => {
      if (child.nodeName === NODENAME) {
        checkboxes.push(child as HTMLChCheckboxElement);
      } else if (child instanceof HTMLElement) {
        checkboxes.push(...this.#getAllCheckboxes(child));
      }
    });

    return checkboxes;
  }
  #addDocument(documentName: string) {
    const documentExists = this.documents.some(
      doc => doc.name === documentName
    );
    if (!documentExists) {
      this.documents = [
        ...this.documents,
        {
          name: documentName,
          deleteEnabled: false,
          deleteRequired: false,
          newEnabled: false,
          newRequired: false,
          readEnabled: false,
          readRequired: false,
          updateEnabled: false,
          updateRequired: false
        }
      ];
      this.#refreshDocumentsActionListState();
    }
  }

  #cancelCallbackHandler = () => {
    this.cancelCallback();
  };
  #confirmCallbackHandler = () => {
    this.confirmCallback(this.documents);
  };

  #documentSuggestValueChangedHandler = async (
    e: ChComboBoxRenderCustomEvent<string> & InputEvent
  ) => {
    const value = e.detail;
    this.suggestDocumentsCallback(value)
      .then(result => {
        this.suggestedDocuments =
          convertSuggestedDocumentsToSuggestItems(result);
      })
      .catch(() => {
        // to do
      });
  };

  #handleCheckboxChange() {
    const data: TaskDocumentData = {
      name: this.selectedDocument.name,
      readEnabled: this.#readEnabledCheckboxEl.value === CHECKED_VALUE,
      readRequired: this.#readRequiredCheckboxEl.value === CHECKED_VALUE,
      newEnabled: this.#newEnabledCheckboxEl.value === CHECKED_VALUE,
      newRequired: this.#newRequiredCheckboxEl.value === CHECKED_VALUE,
      updateEnabled: this.#updateEnabledCheckboxEl.value === CHECKED_VALUE,
      updateRequired: this.#updateRequiredCheckboxEl.value === CHECKED_VALUE,
      deleteEnabled: this.#deleteEnabledCheckboxEl.value === CHECKED_VALUE,
      deleteRequired: this.#deleteRequiredCheckboxEl.value === CHECKED_VALUE
    };

    this.selectedDocument = data;

    this.documents = this.documents.map(doc =>
      doc.name === this.selectedDocument.name ? this.selectedDocument : doc
    );
  }

  #handleDeleteButton = () => {
    this.documents = this.documents.filter(
      doc => doc.name !== this.selectedDocument.name
    );
    this.selectedDocument = null;
    this.#refreshDocumentsActionListState();
  };

  #listenDocumentSelected = async (e: CustomEvent<string>) => {
    if (e.detail) {
      this.#addDocument(e.detail);
    }
  };

  #objectSelectionChangedHandler = (e: CustomEvent) => {
    this.selectedDocument = this.documents.find(
      doc => doc.name === e.detail[0]?.item.caption
    );
  };

  #refreshDocumentsActionListState = () => {
    this.documents?.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
    this.documentsActionListState = taskDocumentsToActionListItemModel(
      this.documents,
      this.selectedDocument
    );

    this.selectedDocument =
      this.selectedDocument || this.documents?.[0] || null;
  };

  #selectDocumentCallbackHandler = async () => {
    const callbackResult = await this.selectDocumentCallback();
    if (callbackResult) {
      this.documentName = callbackResult;
      this.#addDocument(callbackResult);
    }
  };

  #renderActions = () => {
    return (
      <ch-tabular-grid
        class="tabular-grid scrollable"
        rowSelectionMode="none"
        keyboardNavigationMode="focus"
        columnResizeMode="single"
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            class="tabular-grid-column"
            columnId="code"
            columnName=""
            settingable={false}
            sortable={false}
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            class="tabular-grid-column"
            columnId="enabled"
            settingable={false}
            sortable={false}
            columnName={this.#_componentLocale.actions.enabledCheckbox}
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            class="tabular-grid-column"
            columnId="required"
            settingable={false}
            sortable={false}
            columnName={this.#_componentLocale.actions.requiredCheckbox}
          ></ch-tabular-grid-column>
        </ch-tabular-grid-columnset>

        <ch-tabular-grid-row
          // read
          class="tabular-grid-row"
          rowid="read"
        >
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {this.#_componentLocale.actions.readLabel}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            <ch-checkbox
              class="checkbox"
              checkedValue={CHECKED_VALUE}
              value={this.selectedDocument?.readEnabled.toString()}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#readEnabledCheckboxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            <ch-checkbox
              class="checkbox"
              checkedValue={CHECKED_VALUE}
              value={this.selectedDocument?.readRequired.toString()}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#readRequiredCheckboxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </ch-tabular-grid-cell>
        </ch-tabular-grid-row>

        <ch-tabular-grid-row
          // new
          class="tabular-grid-row"
          rowid="read"
        >
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {this.#_componentLocale.actions.newLabel}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            <ch-checkbox
              class="checkbox"
              checkedValue={CHECKED_VALUE}
              value={this.selectedDocument?.newEnabled.toString()}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#newEnabledCheckboxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            <ch-checkbox
              class="checkbox"
              checkedValue={CHECKED_VALUE}
              value={this.selectedDocument?.newRequired.toString()}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#newRequiredCheckboxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </ch-tabular-grid-cell>
        </ch-tabular-grid-row>

        <ch-tabular-grid-row
          // update
          class="tabular-grid-row"
          rowid="read"
        >
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {this.#_componentLocale.actions.updateLabel}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            <ch-checkbox
              class="checkbox"
              checkedValue={CHECKED_VALUE}
              value={this.selectedDocument?.updateEnabled.toString()}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#updateEnabledCheckboxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            <ch-checkbox
              class="checkbox"
              checkedValue={CHECKED_VALUE}
              value={this.selectedDocument?.updateRequired.toString()}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#updateRequiredCheckboxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </ch-tabular-grid-cell>
        </ch-tabular-grid-row>

        <ch-tabular-grid-row
          // delete
          class="tabular-grid-row"
          rowid="read"
        >
          <ch-tabular-grid-cell class="tabular-grid-cell">
            {this.#_componentLocale.actions.deleteLabel}
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            <ch-checkbox
              class="checkbox"
              checkedValue={CHECKED_VALUE}
              value={this.selectedDocument?.deleteEnabled.toString()}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#deleteEnabledCheckboxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </ch-tabular-grid-cell>
          <ch-tabular-grid-cell class="tabular-grid-cell">
            <ch-checkbox
              class="checkbox"
              checkedValue={CHECKED_VALUE}
              value={this.selectedDocument?.deleteRequired.toString()}
              ref={(el: HTMLChCheckboxElement) =>
                (this.#deleteRequiredCheckboxEl = el as HTMLChCheckboxElement)
              }
            ></ch-checkbox>
          </ch-tabular-grid-cell>
        </ch-tabular-grid-row>
      </ch-tabular-grid>
    );
  };

  #renderEmptyState = () => {
    return (
      <gx-ide-empty-state
        class="recent-objects__empty-state"
        stateIconSrc={MODULE_ICON}
        isAnimated={this.#renderedFirstTime}
        stateTitle={this.#_componentLocale.emptyRightPanelInfo.title}
      >
        <button
          class="button-secondary"
          onClick={this.#selectDocumentCallbackHandler}
        >
          {this.#_componentLocale.emptyRightPanelInfo.buttonCaption}
        </button>
      </gx-ide-empty-state>
    );
  };

  #renderFooter = () => {
    return (
      <footer class="footer dialog-footer dialog-footer-with-border spacing-body-block-end spacing-body-inline">
        <div class="buttons-spacer">
          <button
            class="button-secondary"
            id="button-cancel"
            onClick={this.#cancelCallbackHandler}
            part="button-cancel"
          >
            {this.#_componentLocale.footer.cancelButton}
          </button>
          <button
            class="button-primary"
            id="button-create"
            onClick={this.#confirmCallbackHandler}
            part="button-create"
          >
            {this.#_componentLocale.footer.confirmButton}
          </button>
        </div>
      </footer>
    );
  };

  #renderMain = () => {
    return [
      <div class="documents__list spacing-body-inline">
        <ch-action-list-render
          selection="single"
          class="list-box"
          model={this.documentsActionListState}
          onSelectedItemsChange={this.#objectSelectionChangedHandler}
        ></ch-action-list-render>
      </div>,
      this.#renderActions()
    ];
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <div class="main field-group">
            <div class="documents__header buttons-spacer spacing-body-inline spacing-body-block-start">
              <ch-combo-box-render
                class="combo-box documents__suggest"
                model={this.suggestedDocuments}
                placeholder={
                  this.#_componentLocale.documents.suggestPlaceholder
                }
                suggest={true}
                onInput={this.#documentSuggestValueChangedHandler}
                onChange={this.#listenDocumentSelected}
                suggestOptions={{
                  alreadyProcessed: false,
                  autoExpand: true,
                  hideMatchesAndShowNonMatches: false,
                  highlightMatchedItems: false,
                  matchCase: false,
                  regularExpression: false,
                  strict: false
                }}
              ></ch-combo-box-render>

              <span>
                <button
                  class="button-tertiary button-icon-only"
                  aria-label={
                    this.#_componentLocale.documents.addDocumentButtonLabel
                  }
                  title={
                    this.#_componentLocale.documents.addDocumentButtonLabel
                  }
                  onClick={this.#selectDocumentCallbackHandler}
                >
                  <ch-image class="icon-xs" src={MORE_ICON}></ch-image>
                </button>

                <button
                  class="button-tertiary button-icon-only"
                  aria-label={
                    this.#_componentLocale.documents.clearDocumentButtonLabel
                  }
                  title={
                    this.#_componentLocale.documents.clearDocumentButtonLabel
                  }
                  onClick={this.#handleDeleteButton}
                >
                  <ch-image class="icon-xs" src={DELETE_ICON}></ch-image>
                </button>
              </span>
            </div>

            {this.documentsActionListState.length
              ? this.#renderMain()
              : this.#renderEmptyState()}
          </div>
          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}

export type TaskDocumentData = {
  name: string;
  readEnabled: boolean;
  readRequired: boolean;
  newEnabled: boolean;
  newRequired: boolean;
  updateEnabled: boolean;
  updateRequired: boolean;
  deleteEnabled: boolean;
  deleteRequired: boolean;
};

export type SuggestDocumentsCallback = (
  documentName: string
) => Promise<string[] | undefined>;
export type SelectDocumentCallback = () => Promise<string | undefined>;
export type ConfirmCallback = (data: TaskDocumentData[]) => Promise<void>;
export type CancelCallback = () => Promise<void>;
